import React, { forwardRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Container, DisplayText, theme, useResponsive } from '@sg/garnish';
import type { DietaryPreferencesTypes } from '@sg/shared-fe-features-menu';

import { useHomeContent } from '../../hooks';
import { HomeCategory } from '../HomeCategory';
import { HomeProductCard } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategories = forwardRef<View, HomeGenericCategoriesProps>((props, ref) => {
  const {
    genericCategories,
    dietaryPreferences,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { currentBreakpoint, match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Container ref={ref} style={match([styles.containerXS, styles.containerSM])}>
      {genericCategories.map((category) => (
        <View key={category.id} style={styles.category}>
          {currentBreakpoint.isXS ? (
            <BodyText style={styles.titleXS} sizeMatch={['18']}>
              {category.categoryName}
            </BodyText>
          ) : (
            <DisplayText style={styles.titleSM} sizeMatch={['32']}>
              {category.categoryName}
            </DisplayText>
          )}

          <HomeCategory>
            {category.products.slice(0, 3).map((product) => (
              <HomeProductCard
                key={product.lineageKey}
                categoryName={category.categoryName}
                id={product.id}
                slug={product.slug}
                title={product.title}
                cost={product.cost}
                description={product.description}
                contentfulImage={product.image}
                isModifiable={product.isModifiable}
                dietaryPreferences={dietaryPreferences}
                allergenProperties={product.allergenProperties}
                caloriesFromFat={category.showCaloriesFromFat ? product.caloriesFromFat : 0}
                totalFatG={category.showFat ? product.totalFatG : 0}
                totalCarbsG={category.showCarbs ? product.totalCarbsG : 0}
                dietaryFiberG={category.showFiber ? product.dietaryFiberG : 0}
                sugarsG={category.showSugars ? product.sugarsG : 0}
                proteinG={category.showProtein ? product.proteinG : 0}
                co2eKg={category.showCo2 ? product.co2eKg : 0}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={addProductToBag}
                trackItemSelection={trackItemSelection}
              />
            ))}
          </HomeCategory>
        </View>
      ))}
    </Container>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: 0,
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  titleSM: {
    textAlign: 'center',
  },
  category: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeGenericCategoriesProps = {
  genericCategories: ReturnType<typeof useHomeContent>['genericCategories'];
  dietaryPreferences: DietaryPreferencesTypes;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
